/**
 * The texture engine.
 *
 * The globe is not vector geometry: it is a 3072×1536 equirectangular raster
 * where each texel's red channel carries a country id (0 = ocean, 1 = land with
 * no entity, 254 = disputed). Colouring the map means repainting texels through
 * a lookup, and picking a country means reading the id under the cursor. That
 * makes this module the owner of every pixel-level structure — nothing outside
 * touches IDX, the border mask or the image data.
 *
 * The one piece of state that deliberately escapes is `mapView.colorMode`: the
 * legend, the Colour-by chips and assisted discovery all switch which metric the
 * map is showing.
 */
import { byId, TIER_RGB, tierOf } from '../model.js';

export const TW = 3072, TH = 1536;

/** Shared with the panels that change what the map is coloured by. */
export const mapView = { colorMode: 'overall' };   // 'overall' or a dimension name
export const metricOf = c => mapView.colorMode === 'overall' ? c.overall : c.dim[mapView.colorMode];

export const texCanvas = document.createElement('canvas');
texCanvas.width = TW; texCanvas.height = TH;
const tctx = texCanvas.getContext('2d', { willReadFrequently: true });

const BORDER = new Uint8Array(TW * TH);
const bboxById = {};          // id -> [x0,y0,x1,y1], so hover can repaint one country
let IDX;                      // Uint8Array, country id per texel
let texData;                  // ImageData, reused every repaint
let texture;                  // THREE.CanvasTexture, handed over by the scene

const OCEAN = [13, 23, 41], LAND = [33, 40, 53], DISPUTED = [42, 47, 58], DIMF = .22;
const NODATA = [46, 53, 69];  // entity with no assessment for the metric currently on screen
const BRASS = [201, 168, 106];

/* latitude relief: equator breathes, poles recede — baked per row */
const ROWF_SEA = new Float32Array(TH), ROWF_LAND = new Float32Array(TH);
for (let y = 0; y < TH; y++) {
  const t = Math.cos((y / TH - .5) * Math.PI);
  ROWF_SEA[y] = .66 + .5 * t; ROWF_LAND[y] = .88 + .18 * t;
}

let dimSet = null;            // Set of ids to dim (null = none)
let selId = 0, hoverId = 0;

function idColor(id) {
  if (id === 0) return OCEAN;
  if (id === 1) return LAND;
  if (id === 254) return DISPUTED;   // contested territories — assigned to no country
  const c = byId[id]; if (!c) return LAND;
  const v = metricOf(c); if (v == null) return NODATA;
  return TIER_RGB[tierOf(v)];
}

function paintRange(x0, y0, x1, y1) {
  const d = texData.data;
  for (let y = y0; y < y1; y++) {
    let o = (y * TW + x0);
    const fs = ROWF_SEA[y], fl = ROWF_LAND[y];
    for (let x = x0; x < x1; x++, o++) {
      const id = IDX[o];
      let [r, g, b] = idColor(id);
      if (id === 0) { r *= fs; g *= fs; b *= fs }
      else { r *= fl; g *= fl; b *= fl }
      if (id > 1) {
        if (id === hoverId) { r = Math.min(255, r * 1.22 + 14); g = Math.min(255, g * 1.22 + 14); b = Math.min(255, b * 1.22 + 12) }
        if (id === selId) { r = Math.min(255, r * 1.12 + 10); g = Math.min(255, g * 1.12 + 10); b = Math.min(255, b * 1.1 + 6) }
        if (dimSet && dimSet.has(id)) { r = r * DIMF + OCEAN[0] * (1 - DIMF); g = g * DIMF + OCEAN[1] * (1 - DIMF); b = b * DIMF + OCEAN[2] * (1 - DIMF) }
      }
      if (BORDER[o]) {
        if (selId && (id === selId || IDX[o + 1] === selId || IDX[o + TW] === selId)) { r = BRASS[0]; g = BRASS[1]; b = BRASS[2] }
        else { r *= .45; g *= .45; b *= .45 }
      }
      const p = o * 4; d[p] = r; d[p + 1] = g; d[p + 2] = b; d[p + 3] = 255;
    }
  }
}

function graticule() {
  tctx.save();
  tctx.strokeStyle = 'rgba(205,214,230,0.06)'; tctx.lineWidth = 1;
  tctx.beginPath();
  for (let lon = -180; lon <= 180; lon += 15) { const x = (lon + 180) / 360 * TW; tctx.moveTo(x, 0); tctx.lineTo(x, TH) }
  for (let lat = -75; lat <= 75; lat += 15) { const y = (90 - lat) / 180 * TH; tctx.moveTo(0, y); tctx.lineTo(TW, y) }
  tctx.stroke(); tctx.restore();
}

export function repaint(full = true, bx = null) {
  if (!texData) return;   // nothing to paint into until the index map is decoded
  if (full) paintRange(0, 0, TW, TH);
  else if (bx) paintRange(Math.max(0, bx[0] - 2), Math.max(0, bx[1] - 2), Math.min(TW, bx[2] + 3), Math.min(TH, bx[3] + 3));
  tctx.putImageData(texData, 0, 0);
  graticule();
  if (texture) texture.needsUpdate = true;
}

export function setHover(id) {
  if (id === hoverId) return;
  const prev = hoverId; hoverId = id;
  if (prev && bboxById[prev]) repaint(false, bboxById[prev]);
  if (id && bboxById[id]) repaint(false, bboxById[id]);
}

export function setSelected(id) {
  if (id === selId) return;
  selId = id; repaint(true);
}

/** The render loop stops auto-rotating while a country is selected. */
export const selectedId = () => selId;

export function setDim(set) { dimSet = set; repaint(true) }

/** The scene owns the THREE texture; the engine only needs to invalidate it. */
export function attachTexture(t) { texture = t }

/** Country id under a geographic position — the basis of pointer picking. */
export function idAt(lat, lon) {
  const x = Math.min(TW - 1, Math.max(0, Math.round((lon + 180) / 360 * TW)));
  const y = Math.min(TH - 1, Math.max(0, Math.round((90 - lat) / 180 * TH)));
  return IDX[y * TW + x];
}

/**
 * Decode the index map into IDX, derive per-country bounding boxes and the
 * border mask, then paint the first frame.
 */
export function loadIndexMap(url, stage) {
  return new Promise((done, fail) => {
    const img = new Image();
    img.onerror = () => fail(new Error('index map failed to load: ' + url));
    img.onload = () => {
      stage(.25, 'Decoding geography');
      const c = document.createElement('canvas'); c.width = TW; c.height = TH;
      const cx = c.getContext('2d', { willReadFrequently: true });
      cx.drawImage(img, 0, 0);
      const px = cx.getImageData(0, 0, TW, TH).data;
      IDX = new Uint8Array(TW * TH);
      for (let i = 0, o = 0; i < px.length; i += 4, o++) {
        const id = px[i]; IDX[o] = id;
        if (id > 1) {
          const x = o % TW, y = (o / TW) | 0, bb = bboxById[id];
          if (!bb) bboxById[id] = [x, y, x + 1, y + 1];
          else { if (x < bb[0]) bb[0] = x; if (y < bb[1]) bb[1] = y; if (x + 1 > bb[2]) bb[2] = x + 1; if (y + 1 > bb[3]) bb[3] = y + 1 }
        }
      }
      for (let y = 0; y < TH - 1; y++) for (let x = 0; x < TW - 1; x++) {
        const o = y * TW + x, a = IDX[o];
        if ((a !== IDX[o + 1] && (a > 0 || IDX[o + 1] > 0)) || (a !== IDX[o + TW] && (a > 0 || IDX[o + TW] > 0))) BORDER[o] = 1;
      }
      stage(.5, 'Painting the world');
      texData = tctx.createImageData(TW, TH);
      paintRange(0, 0, TW, TH); tctx.putImageData(texData, 0, 0); graticule();
      done();
    };
    img.src = url;
  });
}
